import { NameIdAndUrl } from "@/models/common"
import { Invitation } from "@/models/Invitation"
import { SprintPreview } from "@/models/Sprint"
import { $t } from "@/plugins/i18n"
import { getInitials as getInitialsHelper } from "@/utils/helpers"

import { ShortAccount } from "./Account"
import { TeamPreview } from "./Team"
import { TipsStepType } from "./TipsStep"

export const userInviteSuccessStatuses = [
  "OK",
  "ALREADY_INVITED_IN_CURRENT_ACCOUNT",
  "ALREADY_CREATED_IN_CURRENT_ACCOUNT",
  "NOT_CREATED"
]

export interface Picture {
  pictureStorageKey?: string
  pictureUrl: string | null
}

export interface Notifications {
  itemEmails: NotificationsType
  itemNotifications: NotificationsType
}

export interface NotificationsType {
  addedOrRemoved: boolean
  updated: boolean
  taskTimeReEstimated: boolean
  statusChanged: boolean
  taskChanged: boolean
  descriptionChangedInBacklog: boolean
  dailyDigest: boolean
}

export interface NotificationsSettings {
  title: string
  emailValue: boolean
  notificationValue: boolean
  key: string
}

export interface UserSettings {
  timeZone?: string
  sendEmailNotifications?: boolean
  showTips?: boolean
  showDemo?: boolean
  darkTheme?: boolean
  shownTips?: TipsStepType[]
  showEpicSetupTips?: boolean
  showEpicInlineItemCreationTips?: boolean
  showFirstEntranceOnboardingTips?: boolean
  showFirstItemCreationTips?: boolean
  showMilestoneSetupTips?: boolean
  showOnboardingTips?: boolean
  showRoadMapSetupTips?: boolean
  showSprintSetupTips?: boolean
  showUnprioritizedTips?: boolean
  showProjectTeamTypesTips?: boolean
  showEndTrialPeriod?: boolean
  notifications?: Notifications
  showSingleListViewByProjectAndTeam?: {
    [projectId: string]: {
      [teamId: string]: boolean
    }
  }
}

export interface UserSettingsPayload {
  userId: string
  settings: UserSettings
}

export interface UserWithRole {
  userId: string
  role: string
}

export enum Role {
  CTO = "CTO",
  CIO = "CIO",
  SVP = "SVP",
  VP_ENGINEERING = "VP_ENGINEERING",
  DIRECTOR_ENGINEERING = "DIRECTOR_ENGINEERING",
  HEAD_OF_PRODUCT = "HEAD_OF_PRODUCT",
  PRODUCT_OWNER = "PRODUCT_OWNER",
  PRODUCT_MANAGER = "PRODUCT_MANAGER",
  PROJECT_MANAGER = "PROJECT_MANAGER",
  ENGINEER = "ENGINEER",
  DEV_ENGINEER = "DEV_ENGINEER",
  SCRUM_MASTER = "SCRUM_MASTER",
  MANAGER_ENGINEERING = "MANAGER_ENGINEERING",
  QA = "QA",
  UI_UX = "UI_UX",
  DEVOPS = "DEVOPS",
  DEVOPS_ENGINEER = "DEVOPS_ENGINEER",
  CLOUD_ENGINEER_ARCHITECT = "CLOUD_ENGINEER_ARCHITECT",
  CLOUD_ENGINEER = "CLOUD_ENGINEER",
  BUILD_RELEASE_ENGINEER = "BUILD_RELEASE_ENGINEER",
  BUILD_AND_RELEASE_ENGINEER = "BUILD_AND_RELEASE_ENGINEER",
  SITE_RELIABILITY_ENGINEER = "SITE_RELIABILITY_ENGINEER",
  DEVSECOPS_ENGINEER = "DEVSECOPS_ENGINEER",
  DEV_SEC_OPS_ENGINEER = "DEV_SEC_OPS_ENGINEER",
  DEV_QA = "DEV_QA",
  SYSTEM_NETWORK_ADMIN = "SYSTEM_NETWORK_ADMIN",
  DATA_PLATFORM_ENGINEER = "DATA_PLATFORM_ENGINEER",
  CMO = "CMO",
  VP_DIRECTOR_OF_MARKETING = "VP_DIRECTOR_OF_MARKETING",
  PRODUCT_MARKETING_MANAGER = "PRODUCT_MARKETING_MANAGER",
  BRAND_MANAGER = "BRAND_MANAGER",
  CREATIVE_DIRECTOR = "CREATIVE_DIRECTOR",
  COPYWRITER = "COPYWRITER",
  CONTENT_MARKETING_MANAGER = "CONTENT_MARKETING_MANAGER",
  VIDEO_PRODUCER_EDITOR = "VIDEO_PRODUCER_EDITOR",
  GRAPHICS_MOTION_DESIGNER = "GRAPHICS_MOTION_DESIGNER",
  UI_UX_DESIGNER = "UI_UX_DESIGNER",
  SEO_SPECIALIST = "SEO_SPECIALIST",
  PAID_MEDIA_MANAGER = "PAID_MEDIA_MANAGER",
  EMAIL_MARKETING_MANAGER = "EMAIL_MARKETING_MANAGER",
  SOCIAL_MEDIA_MANAGER = "SOCIAL_MEDIA_MANAGER",
  WEB_ANALYTICS_SPECIALIST = "WEB_ANALYTICS_SPECIALIST",
  WEB_DEVELOPER = "WEB_DEVELOPER",
  PR_MEDIA_RELATIONS = "PR_MEDIA_RELATIONS",
  CPO = "CPO",
  VP_OF_PRODUCT = "VP_OF_PRODUCT",
  DIR_OF_PRODUCT = "DIR_OF_PRODUCT",
  UI_UX_HEAD_OF_PRODUCT = "UI_UX_HEAD_OF_PRODUCT",
  HEAD_OF_DESIGN = "HEAD_OF_DESIGN",
  UI_UX_PRODUCT_OWNER = "UI_UX_PRODUCT_OWNER",
  UI_UX_PRODUCT_MANAGER = "UI_UX_PRODUCT_MANAGER",
  UI_UX_PROJECT_MANAGER = "UI_UX_PROJECT_MANAGER",
  UI_UX_LEAD = "UI_UX_LEAD",
  UI_UI_UX_DESIGNER = "UI_UI_UX_DESIGNER",
  GRAPHIC_MOTION_DESIGNER = "GRAPHIC_MOTION_DESIGNER"
}

export enum DeveloperRoles {
  QA = "QA",
  ENGINEER = "ENGINEER",
  DEV_ENGINEER = "DEV_ENGINEER",
  UI_UX = "UI_UX",
  DEVOPS = "DEVOPS"
}

export enum ConvertRoleWithAccess {
  MANAGER_ENGINEERING = "ENGINEERING_MANAGERS",
  VP_ENGINEERING = "ENGINEERING_MANAGERS",
  PRODUCT_OWNER = "PRODUCT_OWNERS_AND_PM",
  PRODUCT_MANAGER = "PRODUCT_OWNERS_AND_PM",
  PROJECT_MANAGER = "PRODUCT_OWNERS_AND_PM",
  SCRUM_MASTER = "SCRUM_MASTER",
  HEAD_OF_PRODUCT = "HEAD_OF_PRODUCT"
}

export enum AccountPermission {
  ADMIN = "ADMIN",
  USER = "USER"
}

export interface Roles {
  [key: string]: Role
}

export interface AccountPermissions {
  [key: string]: AccountPermission
}

export interface Permission {
  [key: string]: PermissionParams
}

export interface PermissionParams {
  timesheetApprovalEnabled: boolean
}

export interface ExternalResourceId {
  id: string
  importId: string
}

export class User {
  id: string
  email: string
  enabled: boolean
  username: string
  firstName: string
  lastName: string
  picture: Picture
  pictureUrl: string
  imported: boolean
  teamRole: string
  accountRole: string
  type: string

  teamRoles: Roles
  accountRoles: Roles
  roles: string[]
  accountPermissions: AccountPermissions
  teams?: TeamPreview[]
  projects?: NameIdAndUrl[]
  accounts: ShortAccount[]
  lastLogin?: Date
  timeZone?: string
  sprints: SprintPreview[]

  teamRolesString: string
  accountRolesString: string
  permissions: Permission
  settings?: UserSettings

  invited: boolean
  inviteId: string
  emailUnreachable: boolean
  emailVerified: boolean
  superAdmin: boolean
  jiraApiToken?: string
  externalResourceId?: ExternalResourceId
  inSynchronizedProject?: boolean
  roleType: string
  teamIdsAndRoles?: {
    [key: string]: Role
  }
  itemCreationAccessByTeamId?: {
    [teamId: string]: string
  }

  constructor(props?: Partial<User>) {
    this.set(props)
  }

  getJsonObj() {
    return {
      firstName: this.firstName.trim(),
      lastName: this.lastName.trim(),
      email: this.email,
      username: `${this.firstName} ${this.lastName}`,
      pictureUrl: null,
      teamRole: this.teamRole,
      accountRole: this.accountRole,
      roles: this.roles,
      type: this.type,
      enabled: this.enabled,
      imported: this.imported
    }
  }

  getUpdateJsonObj(accountId: string) {
    return {
      firstName: this.firstName.trim(),
      lastName: this.lastName.trim(),
      email: this.email,
      accountRole: this.getRole(accountId),
      accountPermission: this.getPermission(accountId),
      enabled: this.enabled,
      imported: this.imported
    }
  }

  static create(props?: User): User {
    return new User(props)
  }

  set(props?: Partial<User>): void {
    this.setDefaultData()

    if (!props) return

    this.id = props.id ?? ""
    this.email = props.email ?? ""
    this.enabled = props.enabled ?? false
    this.firstName = props.firstName ?? ""
    this.lastName = props.lastName ?? ""
    this.username = props.username ?? this.fullName
    this.pictureUrl =
      props.picture && props.picture.pictureUrl
        ? props.picture.pictureUrl + "?" + new Date().getTime()
        : ""
    this.picture = props.picture ?? { pictureUrl: null }

    this.teamRole = props.teamRole ?? ""
    this.accountRoles = props.accountRoles ?? {}
    this.type = props.type ?? ""

    this.accountPermissions = props.accountPermissions ?? {}
    this.teamRolesString = props.teamRoles ? this.getRolesString(props.teamRoles) : "N_A"
    this.accountRolesString = props.accountRoles ? this.getRolesString(props.accountRoles) : "N_A"

    this.teams = props.teams ?? []
    this.projects = props.projects ?? []
    this.accounts = props.accounts ?? []
    this.settings = props.settings

    this.invited = props.invited ?? false
    this.inviteId = props.inviteId ?? ""
    this.emailUnreachable = props.emailUnreachable ?? false
    this.superAdmin = props.superAdmin ?? false
    this.emailVerified = props.emailVerified ?? true

    this.accountRole = props.accountRole ?? "N_A"
    this.roles = props.roles ?? []
    this.permissions = props.permissions ?? {}

    this.lastLogin = props.lastLogin ?? undefined
    this.timeZone = props.settings?.timeZone ?? props.timeZone ?? undefined
    this.jiraApiToken = props.jiraApiToken ?? undefined
    this.externalResourceId = props.externalResourceId ?? undefined
    this.inSynchronizedProject = props.inSynchronizedProject ?? undefined
    this.roleType = props.roleType ?? ""
    this.sprints = props.sprints ?? []
    this.teamIdsAndRoles = props.teamIdsAndRoles ?? undefined
    this.itemCreationAccessByTeamId = props.itemCreationAccessByTeamId ?? undefined
  }

  setDefaultData(): void {
    this.id = ""
    this.email = ""
    this.enabled = true
    this.username = ""
    this.firstName = ""
    this.lastName = ""
    this.pictureUrl = ""

    this.teamRole = ""
    this.accountRole = ""
    this.type = "USER"

    this.teamRoles = {}
    this.accountRoles = {}
    this.accountPermissions = {}
    this.teams = []
    this.accounts = []
    this.projects = []

    this.invited = false
    this.inviteId = ""
    this.emailUnreachable = false
    this.superAdmin = false
    this.emailVerified = true
  }

  static createDefault(): User {
    return new User()
  }

  static createFromInvitation(props: Invitation) {
    return new User({
      username: props.inviteeName,
      type: props.inviteeAccountRole,
      accountRole: props.inviteeTeamRole,
      teamRole: props.inviteeTeamRole,
      email: props.inviteeEmail,
      invited: true,
      inviteId: props.id,
      teamIdsAndRoles: props.teamIdsAndRoles
    })
  }

  getInitials(): string {
    return getInitialsHelper(
      this.firstName.length && this.lastName.length
        ? `${this.firstName} ${this.lastName}`
        : this.username
    )
  }

  get accountRolesTitleString() {
    return $t(`role.${this.accountRole}`)
  }

  getRolesString(obj: Roles): string {
    if (!obj) {
      return "None"
    }

    const arr = Object.values(obj).filter(a => !!a)
    if (arr.length === 0) {
      return "None"
    }
    return [...new Set(arr.map(a => $t(`role['${a}']`)))].join(", ")
  }

  isAdmin(accountId: string) {
    return this.accountPermissions[accountId] === AccountPermission.ADMIN
  }

  getRole(accountId: string) {
    return this.accountRoles[accountId]
  }

  getPermission(accountId: string) {
    return this.accountPermissions[accountId]
  }

  get fullName(): string {
    return this.firstName || this.lastName
      ? `${this.firstName.trim()} ${this.lastName.trim()}`.trim()
      : ""
  }

  get accountIds(): string[] {
    return Object.keys(this.accountRoles)
  }

  get accountId(): string | undefined {
    return this.accountIds[0]
  }

  get userTeamRole() {
    return $t(`role.${this.teamRole}`) ?? $t("none")
  }

  get accountName() {
    return this.accounts.find(a => a.id === this.accountId)?.urlName ?? ""
  }

  static get rolesList() {
    return Object.values(Role)
  }
}

export class InvitedUser {
  email: string
  username: string
  firstName: string
  lastName: string
  sendError: boolean
  pictureUrl: string

  constructor(props?: Partial<User>) {
    this.set(props)
  }

  setDefaultData(): void {
    this.sendError = false
    this.email = ""
    this.username = ""
    this.firstName = ""
    this.lastName = ""
    this.pictureUrl = ""
  }

  get fullName(): string {
    return this.firstName || this.lastName
      ? `${this.firstName.trim()} ${this.lastName.trim()}`.trim()
      : ""
  }

  set(props?: Partial<User>): void {
    this.setDefaultData()

    if (!props) return

    this.email = props.email ?? ""
    this.firstName = props.firstName ?? ""
    this.lastName = props.lastName ?? ""
    this.username = props.username ?? this.fullName
    this.pictureUrl =
      props.picture && props.picture.pictureUrl
        ? props.picture.pictureUrl + "?" + new Date().getTime()
        : ""
  }

  getInitials(): string {
    return getInitialsHelper(
      this.firstName.length && this.lastName.length
        ? `${this.firstName} ${this.lastName}`
        : this.username
    )
  }
}
